import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

type ToastTone = 'ok' | 'crit'

interface ToastItem {
  id: number
  tone: ToastTone
  message: string
}

interface ToastApi {
  /** Confirmação de escrita: "licença revogada", "empresa criada". */
  success: (message: string) => void
  /** Falha de uma **ação**. Falha de carga é `ErrorState`, no lugar do conteúdo. */
  failure: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

/** Tempo suficiente para ler duas linhas sem virar decoração permanente. */
const DISMISS_MS = 6_000

/**
 * Confirmações efêmeras.
 *
 * Existem porque quase toda ação destas telas acontece dentro de um diálogo que
 * se fecha: sem o aviso, revogar uma licença é indistinguível de o diálogo ter
 * fechado sozinho. A lista que está por trás também recarrega, mas numa tabela
 * de 50 linhas essa mudança não é vista.
 *
 * `aria-live="polite"` e não `assertive`: a confirmação não deve interromper o
 * que o leitor de tela está falando no momento.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current++
    setItems((current) => [...current, { id, tone, message }])
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => push('ok', message),
      failure: (message: string) => push('crit', message),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-3 bottom-3 z-60 flex w-80 max-w-[calc(100vw-1.5rem)] flex-col gap-1.5"
      >
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(item.id), DISMISS_MS)
    return () => clearTimeout(timer)
  }, [item.id, onDismiss])

  return (
    <div
      className={`alert-enter pointer-events-auto flex items-start gap-2 border-l-2 bg-raised px-3 py-2 text-sm shadow-overlay ${
        item.tone === 'ok' ? 'border-ok text-fg-secondary' : 'border-crit text-crit'
      }`}
    >
      <span className="min-w-0 flex-1 leading-snug">{item.message}</span>
      <button
        type="button"
        aria-label="Dispensar aviso"
        onClick={() => onDismiss(item.id)}
        className="shrink-0 text-fg-muted hover:text-fg"
      >
        ✕
      </button>
    </div>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (api === null) throw new Error('useToast precisa estar dentro de <ToastProvider>.')
  return api
}
